import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, MapPin, Briefcase } from "lucide-react";
import { BRAND, TARGET_ROLES } from "../data/content";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { useJsonLd, PERSON_SCHEMA } from "../hooks/useJsonLd";
import CVBlock from "../components/CVBlock";

const SNAPSHOT = [
  { label: "Career span", value: "13+ yrs" },
  { label: "Leadership", value: "Cross-functional teams up to 94 FTE" },
  { label: "Event scale", value: "145,000+ attendees · 525+ events / year" },
  { label: "Budget stewardship", value: "$2.8M ops · $1.75M festival · $1.2M sponsorship" },
  { label: "Countries", value: "Australia · Canada" },
  { label: "Canada-ready", value: "AODA-aware · MHFA (Canada) · Trauma-informed" },
  { label: "Rights", value: "Australian citizen — unrestricted Australian work rights" },
  { label: "Availability", value: "Open to senior roles" },
];

export default function Recruiter() {
  useDocumentMeta({
    title: "For Recruiters · Senior roles — JJ Argent",
    description:
      "Considering JJ for a senior role? Snapshot of scale, role categories, an instant Executive CV download and one-click LinkedIn. Open to senior programs, projects and operations roles.",
    path: "/for-recruiters",
  });
  useJsonLd("person-recruiter", { ...PERSON_SCHEMA, seeks: "Senior program, events or operations role" });
  return (
    <main id="main" className="bg-[#0B0B0C] text-[#FAF9F6]" data-testid="recruiter-page">
      {/* Hero */}
      <section className="pt-40 pb-16 relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p className="eyebrow">For recruiters</p>
              <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.04em]">
                Considering JJ{" "}<br />
                <span className="text-[#D8A936]">for a role?</span>
              </h1>
              <p className="mt-8 text-lg leading-relaxed text-[#A9A9A6] max-w-2xl">
                Everything you need in one place — a snapshot of scale, the role categories JJ is open to,
                and the current Executive CV as an instant download.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <a
                href={BRAND.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="recruiter-linkedin"
                className="btn-primary justify-center"
              >
                <Linkedin size={16} /> Connect on LinkedIn
              </a>
              <Link
                to="/contact"
                data-testid="recruiter-request-conversation"
                className="btn-ghost justify-center"
              >
                Request a conversation <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Snapshot */}
      <section className="border-t border-[#343437] py-20" data-testid="recruiter-snapshot">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="eyebrow">Snapshot</p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight text-[#FAF9F6]">
            The numbers behind the CV.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#343437] border border-[#343437]">
            {SNAPSHOT.map((s) => (
              <div key={s.label} className="bg-[#0B0B0C] p-6 lg:p-8" data-testid={`snapshot-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="text-[10px] tracking-[0.24em] uppercase text-[#A9A9A6]">{s.label}</p>
                <p className="mt-3 font-display text-lg leading-tight text-[#FAF9F6]">{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target roles */}
      <section className="border-t border-[#343437] py-20 bg-[#151517]" data-testid="recruiter-target-roles">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-6">
              <p className="eyebrow">Roles I'm open to</p>
              <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[#FAF9F6]">
                Target role{" "}<br />categories.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-12">
              <p className="text-[#A9A9A6] max-w-lg leading-relaxed">
                Senior, cross-functional roles that require someone who can build systems, lead people
                and deliver at scale — inside universities, community organisations or complex operational teams.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TARGET_ROLES.map((role) => (
              <div
                key={role}
                data-testid={`target-role-${role.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="border border-[#343437] px-5 py-4 flex items-center gap-3 hover:border-[#D8A936] hover:-translate-y-0.5 hover:bg-[#0B0B0C] transition-all duration-500"
              >
                <Briefcase size={14} className="text-[#D8A936] shrink-0" />
                <span className="font-display text-[15px] text-[#FAF9F6]">{role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CV delivery — hybrid: instant download + tailored request form */}
      <section className="border-t border-[#343437] py-24" data-testid="cv-delivery">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-6">
              <p className="eyebrow">The CV</p>
              <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[#FAF9F6]">
                One canonical file.{" "}<br />
                <span className="text-[#D8A936]">Zero friction.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-12">
              <p className="text-[#A9A9A6] max-w-lg leading-relaxed">
                The current Executive CV covers every senior engagement across universities, festivals,
                hospitality and corporate operations. Download instantly — or ask for a version rewritten for your role.
              </p>
            </div>
          </div>

          <div className="max-w-2xl">
            <CVBlock />
          </div>
        </div>
      </section>

      {/* Where I've worked */}
      <section className="border-t border-[#343437] py-20 bg-[#151517]" data-testid="recruiter-geography">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="eyebrow">Where I've worked</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight text-[#FAF9F6]">
              Australia{" "}<br />
              <span className="text-[#D8A936]">and Canada.</span>
            </h2>
            <p className="mt-6 text-[#A9A9A6] leading-relaxed">
              Live experience delivering programs and events on both sides of the Pacific. Open to
              other markets on request.
            </p>
          </div>
          <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { place: "Melbourne", tag: "Home base · Monash · UniMelb Union" },
              { place: "Toronto", tag: "U of T Ancillary Services · 94 FTE · $2.8M" },
              { place: "Calgary", tag: "Calgary Pride · 145K attendees · $1.75M" },
              { place: "Cairns / QLD", tag: "JCU · Redlynch Valley Community" /* TODO(JJ): "Cancer Council" removed — Batch 2 §9.4 not substantiated by CV */ },
            ].map((p) => (
              <li key={p.place} className="border border-[#343437] p-5 flex items-start gap-4">
                <MapPin size={16} className="text-[#D8A936] mt-1 shrink-0" />
                <div>
                  <p className="font-display text-lg text-[#FAF9F6]">{p.place}</p>
                  <p className="text-sm text-[#A9A9A6] mt-1">{p.tag}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-[#343437]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div>
            <p className="eyebrow">Next step</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight tracking-tight">
              Let's have a{" "}<br />20-minute chat.
            </h2>
            <p className="mt-4 text-[#A9A9A6] max-w-lg">
              Send the role brief. I'll come to the call with three ways I'd approach the mandate in the first 90 days.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="recruiter-cta-contact" className="btn-primary">
              Request a conversation <ArrowUpRight size={16} />
            </Link>
            <a
              href={BRAND.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="recruiter-cta-linkedin"
              className="btn-ghost"
            >
              <Linkedin size={14} /> LinkedIn
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
